import type {CreateExtArgs} from '@wix/document-manager-core'
import type {Callback1, IdMap, IdMapValue} from '@wix/document-services-types'
import {Handler, HandlerResult, HandlerStatus, PayloadData, PayloadStatus} from './eventHandlerExtension'
import type {FileUploadExtApi} from './fileUploadExtension'

export interface UploadContext<TOriginalData> {
    createExtArgs: CreateExtArgs
    idMap: IdMap
    originalData: Record<string, TOriginalData>
    onSuccess?: Callback1<any>
    onError?: Callback1<any>
    failures: string[]
    totalItems: number
    processedItems: number
}

export interface UploadConfig<TTransformed> {
    extractData(output: any): any | undefined
    transformData(data: any): Promise<TTransformed> | TTransformed
    injectItem(createExtArgs: CreateExtArgs, idMapValue: IdMapValue, data: TTransformed): void
    errorMessage: string
    timeOutInMS?: number
}

export const createUploadContext = <TOriginalData>(params: {
    createExtArgs: CreateExtArgs
    idMap: IdMap
    originalData: Record<string, TOriginalData>
    onSuccess?: Callback1<any>
    onError?: Callback1<any>
    failures?: string[]
    totalItems: number
}): UploadContext<TOriginalData> => ({
    ...params,
    failures: params.failures ?? [],
    processedItems: 0
})

const checkUploadCompletionAndCallbacks = <TOriginalData>(context: UploadContext<TOriginalData>) => {
    const {processedItems, totalItems, failures, onError, onSuccess, createExtArgs} = context

    if (processedItems === totalItems) {
        createExtArgs.dal.commitTransaction()
        if (failures.length > 0) {
            onError?.({errors: failures})
        } else {
            onSuccess?.('success')
        }
    }
}

const handleGeneralUploadError = <TOriginalData, TTransformed>(
    context: UploadContext<TOriginalData>,
    config: UploadConfig<TTransformed>,
    errorMessage: string
): HandlerResult => {
    const {originalData, idMap, createExtArgs, failures, totalItems} = context

    Object.entries(idMap).forEach(([mappedId, value]) => {
        config.injectItem(createExtArgs, value, originalData[mappedId] as unknown as TTransformed)
    })

    failures.push(errorMessage)
    context.processedItems = totalItems

    checkUploadCompletionAndCallbacks(context)
    return {status: HandlerStatus.FAILED, reason: errorMessage}
}

const handleSpecificItemUploadError = <TOriginalData, TTransformed>(
    mappedId: string,
    context: UploadContext<TOriginalData>,
    config: UploadConfig<TTransformed>,
    errorMessage: string
): HandlerResult => {
    const {originalData, idMap, createExtArgs, failures} = context
    config.injectItem(createExtArgs, idMap[mappedId], originalData[mappedId] as unknown as TTransformed)
    failures.push(errorMessage)
    context.processedItems++
    checkUploadCompletionAndCallbacks(context)
    return {status: HandlerStatus.FAILED, reason: errorMessage}
}

const createUploadCallback = <TOriginalData, TTransformed>(
    mappedId: string,
    context: UploadContext<TOriginalData>,
    config: UploadConfig<TTransformed>
): Handler => {
    return async (uploadData: PayloadData): Promise<HandlerResult> => {
        const {status, output} = uploadData ?? {}
        const extractedData = config.extractData(output)

        if (status === PayloadStatus.COMPLETED && extractedData) {
            try {
                const transformedData = await config.transformData(extractedData)
                config.injectItem(context.createExtArgs, context.idMap[mappedId], transformedData)
                context.processedItems++
                checkUploadCompletionAndCallbacks(context)
                return {status: HandlerStatus.COMPLETED}
            } catch (error) {
                return handleSpecificItemUploadError(mappedId, context, config, `${config.errorMessage}: ${error}`)
            }
        }

        return handleSpecificItemUploadError(mappedId, context, config, config.errorMessage)
    }
}

export const uploadFilesWithHandlers = async <TOriginalData, TTransformed>(
    context: UploadContext<TOriginalData>,
    config: UploadConfig<TTransformed>,
    items: Record<string, string>
): Promise<HandlerResult | void> => {
    try {
        const {fileUpload} = context.createExtArgs.extensionAPI as FileUploadExtApi
        const {handlers, urls} = Object.entries(items).reduce(
            (acc, [mappedId, url]) => {
                acc.handlers.push(createUploadCallback(mappedId, context, config))
                acc.urls.push(url)
                return acc
            },
            {handlers: [] as Handler[], urls: [] as string[]}
        )
        await fileUpload.uploadFiles(handlers, urls, config.timeOutInMS)
    } catch (error) {
        return handleGeneralUploadError(context, config, `trying to call upload failed: ${error}`)
    }
}

export const uploadSingleFileWithHandler = async <TOriginalData, TTransformed>(
    context: UploadContext<TOriginalData>,
    config: UploadConfig<TTransformed>,
    mappedId: string,
    url: string
): Promise<HandlerResult | void> => {
    try {
        const {fileUpload} = context.createExtArgs.extensionAPI as FileUploadExtApi
        const callback = createUploadCallback(mappedId, context, config)
        await fileUpload.uploadFile(callback, url, config.timeOutInMS)
    } catch (error) {
        return handleSpecificItemUploadError(mappedId, context, config, `trying to call upload failed: ${error}`)
    }
}
